/**
 * Numbers → German words, following the Duden.
 *
 * Rules implemented (Duden, entries "hundert", "tausend", "Million" and the section on
 * Zahlwörter in the Rechtschreibung):
 *
 * - Numbers below a million are one word, in lower case: zweihundertdreiundzwanzig,
 *   dreitausendvierhundert. The unit comes before the ten, joined with "und":
 *   einundzwanzig, nicht "zwanzigeins".
 * - Hundred and thousand may carry "ein-": [ein]hundert, [ein]tausend, [ein]hunderttausend.
 * - Million, Milliarde, Billion, Billiarde, Trillion and Trilliarde are feminine nouns:
 *   capitalised, written apart, with "eine" for one ("eine Million dreihunderttausend") and a
 *   plural from two ("zwei Millionen"). The scale is the long one: a Milliarde is 10^9 and a
 *   Billion is 10^12.
 * - A final 1 is "eins" when counting and "ein / eine / einen" before a noun; the 1 inside
 *   einundzwanzig is always "ein".
 * - Ordinals: erste, zweite, dritte, vierte, fünfte, sechste, siebte (siebente), achte …
 *   zwölfte; 13–19 add -t (dreizehnte); from 20 add -st (zwanzigste, hundertste).
 * - ß is written ss in Switzerland and Liechtenstein: dreissig.
 */

import { isWholeDigits, splitGroups } from "./number-input";

export type GermanOne = "eins" | "ein" | "eine" | "einen";
export type GermanOrdinalEnding = "e" | "er" | "es" | "en" | "em";

export interface GermanOptions {
  /** How a final 1 is written: eins when counting, ein / eine / einen before a noun. */
  one: GermanOne;
  /** Write "einhundert" and "eintausend" instead of "hundert" and "tausend". */
  einPrefix: boolean;
  /** Swiss and Liechtenstein spelling: dreissig instead of dreißig. */
  swiss: boolean;
}

export type GermanNote =
  | "oneWord"
  | "unitFirst"
  | "einPrefix"
  | "oneForm"
  | "millionApart"
  | "longScale"
  | "swiss";

export interface GermanCardinal {
  words: string;
  notes: GermanNote[];
}

const UNITS = [
  "null",
  "eins",
  "zwei",
  "drei",
  "vier",
  "fünf",
  "sechs",
  "sieben",
  "acht",
  "neun",
  "zehn",
  "elf",
  "zwölf",
  "dreizehn",
  "vierzehn",
  "fünfzehn",
  "sechzehn",
  "siebzehn",
  "achtzehn",
  "neunzehn",
] as const;

const TENS = [
  "",
  "",
  "zwanzig",
  "dreißig",
  "vierzig",
  "fünfzig",
  "sechzig",
  "siebzig",
  "achtzig",
  "neunzig",
] as const;

/** Singular and plural names of the 10^6, 10^9 … 10^21 groups. */
const SCALES: readonly (readonly [string, string])[] = [
  ["", ""],
  ["", ""],
  ["Million", "Millionen"],
  ["Milliarde", "Milliarden"],
  ["Billion", "Billionen"],
  ["Billiarde", "Billiarden"],
  ["Trillion", "Trillionen"],
  ["Trilliarde", "Trilliarden"],
];

/** 1–99 as one word; `one` is the word used when the number is exactly 1 or ends in a lone 1. */
function underHundred(n: number, one: string): string {
  if (n === 1) {
    return one;
  }
  if (n < 20) {
    return UNITS[n];
  }
  const unit = n % 10;
  const ten = TENS[Math.floor(n / 10)];
  if (unit === 0) {
    return ten;
  }
  return `${unit === 1 ? "ein" : UNITS[unit]}und${ten}`;
}

/** 1–999 as one word. */
function underThousand(v: number, one: string, einPrefix: boolean): string {
  const h = Math.floor(v / 100);
  const rest = v % 100;
  let out = "";
  if (h === 1) {
    out = einPrefix ? "einhundert" : "hundert";
  } else if (h > 1) {
    out = `${UNITS[h]}hundert`;
  }
  return rest > 0 ? `${out}${underHundred(rest, one)}` : out;
}

function oneWord(form: GermanOne): string {
  return form;
}

function buildWords(digits: string, options: GermanOptions): string {
  if (digits === "0") {
    return "null";
  }
  const groups = splitGroups(digits, 3);
  const scaled: string[] = [];
  let low = "";
  for (let i = groups.length - 1; i >= 0; i--) {
    const v = groups[i];
    if (v === 0) {
      continue;
    }
    if (i >= 2) {
      scaled.push(
        v === 1
          ? `eine ${SCALES[i][0]}`
          : `${underThousand(v, "ein", options.einPrefix)} ${SCALES[i][1]}`
      );
    } else if (i === 1) {
      low +=
        v === 1
          ? `${options.einPrefix ? "ein" : ""}tausend`
          : `${underThousand(v, "ein", options.einPrefix)}tausend`;
    } else {
      low += underThousand(v, oneWord(options.one), options.einPrefix);
    }
  }
  const words = [...scaled, low].filter((x) => x !== "").join(" ");
  return options.swiss ? words.replaceAll("ß", "ss") : words;
}

const RE_UNIT_FIRST = /[a-zäöü]+und[a-zäöü]+/;
const RE_ONE_WORD = /[a-zäöü]{12,}/;
const RE_EIN_PREFIX = /(?:^|\s)ein(?:hundert|tausend)/;
const RE_FINAL_ONE = /(?:^|[a-zäöü])(?:eins|ein|eine|einen)$/;

function collectNotes(
  digits: string,
  words: string,
  options: GermanOptions
): GermanNote[] {
  const notes: GermanNote[] = [];
  if (RE_UNIT_FIRST.test(words)) {
    notes.push("unitFirst");
  }
  if (RE_ONE_WORD.test(words)) {
    notes.push("oneWord");
  }
  if (RE_EIN_PREFIX.test(words)) {
    notes.push("einPrefix");
  }
  if (RE_FINAL_ONE.test(words)) {
    notes.push("oneForm");
  }
  if (digits.length >= 7) {
    notes.push("millionApart");
  }
  if (digits.length >= 10) {
    notes.push("longScale");
  }
  if (options.swiss && words.includes("ss")) {
    notes.push("swiss");
  }
  return notes;
}

/** The cardinal for a whole number from 0 to 10^24 − 1, given as decimal digits. */
export function germanCardinal(
  digits: string,
  options: GermanOptions
): GermanCardinal {
  if (!isWholeDigits(digits)) {
    throw new RangeError("germanCardinal: out of range");
  }
  const words = buildWords(digits, options);
  return {
    words,
    notes: digits === "0" ? [] : collectNotes(digits, words, options),
  };
}

const NON_ZERO_DIGIT = /[1-9]/;

/** Decimal digits read one by one: "drei Komma eins vier". */
export function germanDecimalDigits(fraction: string): string {
  return [...fraction].map((d) => UNITS[Number(d)]).join(" ");
}

/** A signed, possibly decimal number: "minus drei Komma eins vier". */
export function germanNumber(
  negative: boolean,
  integer: string,
  fraction: string,
  options: GermanOptions
): GermanCardinal {
  const decimal = fraction !== "" && NON_ZERO_DIGIT.test(fraction);
  const whole = germanCardinal(
    integer,
    decimal ? { ...options, one: "eins" } : options
  );
  let words = whole.words;
  if (decimal) {
    const tail = germanDecimalDigits(fraction);
    words = `${words} Komma ${options.swiss ? tail.replaceAll("ß", "ss") : tail}`;
  }
  return {
    words: negative ? `minus ${words}` : words,
    notes: decimal ? [] : whole.notes,
  };
}

// ---------------------------------------------------------------------------
// Ordinals

export const MAX_GERMAN_ORDINAL = 999_999;

export interface GermanOrdinalOptions {
  ending: GermanOrdinalEnding;
  einPrefix: boolean;
  swiss: boolean;
}

export interface GermanOrdinal {
  /** The ordinal with its ending: "einundzwanzigste". */
  words: string;
  /** The stem without an ending: "einundzwanzigst". */
  stem: string;
  /** "21." — the written form in German. */
  abbreviation: string;
  alternative: string | null;
}

const ORDINAL_STEMS: Record<number, string> = {
  1: "erst",
  2: "zweit",
  3: "dritt",
  4: "viert",
  5: "fünft",
  6: "sechst",
  7: "siebt",
  8: "acht",
  9: "neunt",
  10: "zehnt",
  11: "elft",
  12: "zwölft",
};

/** The ordinal stem for 1–99: erst … zwölft, then -t up to 19 and -st from 20. */
function ordinalStemUnderHundred(n: number): string {
  const known = ORDINAL_STEMS[n];
  if (known) {
    return known;
  }
  const cardinal = underHundred(n, "eins");
  return n < 20 ? `${cardinal}t` : `${cardinal}st`;
}

/** Everything before the last two digits, as the one-word prefix of the ordinal. */
function ordinalPrefix(n: number, einPrefix: boolean): string {
  const base = n - (n % 100);
  if (base === 0) {
    return "";
  }
  return buildWords(String(base), {
    one: "ein",
    einPrefix,
    swiss: false,
  });
}

function ordinalStem(n: number, einPrefix: boolean): string {
  if (n % 100 === 0) {
    return `${buildWords(String(n), { one: "ein", einPrefix, swiss: false })}st`;
  }
  return ordinalPrefix(n, einPrefix) + ordinalStemUnderHundred(n % 100);
}

/** The ordinal for 1 to 999,999, or null outside that range. */
export function germanOrdinal(
  n: number,
  options: GermanOrdinalOptions
): GermanOrdinal | null {
  if (!Number.isInteger(n) || n < 1 || n > MAX_GERMAN_ORDINAL) {
    return null;
  }
  const finish = (stem: string) => {
    const text = `${stem}${options.ending}`;
    return options.swiss ? text.replaceAll("ß", "ss") : text;
  };
  const stem = ordinalStem(n, options.einPrefix);
  const alternative = n % 100 === 7 ? finish(`${stem.slice(0, -1)}ent`) : null;
  return {
    words: finish(stem),
    stem: options.swiss ? stem.replaceAll("ß", "ss") : stem,
    abbreviation: `${n}.`,
    alternative,
  };
}
